import { useState, FormEvent } from 'react';
import PageLayout from '../../../components/feature/PageLayout';
import PageHero from '../../../components/feature/PageHero';
import AnnouncementBuilder, { BuilderData } from '../../../components/feature/AnnouncementBuilder';
import { submitLocalForm } from '../../../lib/localForms';

export default function EngagementFormPage() {
  const [formData, setFormData] = useState({
    bridegroomName: '',
    bridegroomFamilyName: '',
    bridegroomFatherName: '',
    bridegroomFatherFamilyName: '',
    bridegroomMotherName: '',
    bridegroomMotherFamilyName: '',
    brideName: '',
    brideFamilyName: '',
    brideFatherName: '',
    brideFatherFamilyName: '',
    brideMotherName: '',
    brideMotherFamilyName: '',
    location: 'Saint Mina & Pope Cyril VI COC',
    priest: 'Father Anthony',
    priestOther: '',
    locationOther: '',
    eventDate: '',
    eventTime: '',
  });

  const [posterMode, setPosterMode] = useState<'builder' | 'upload'>('builder');
  const [builderData, setBuilderData] = useState<BuilderData>({ photoDataUrl: '', customMessage: '', imagePosition: 'center', themeIndex: 0, hasPhoto: false });
  const [customPosterFile, setCustomPosterFile] = useState<File | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const requiredFields = ['bridegroomName','bridegroomFamilyName','bridegroomFatherName','bridegroomFatherFamilyName','bridegroomMotherName','bridegroomMotherFamilyName','brideName','brideFamilyName','brideFatherName','brideFatherFamilyName','brideMotherName','brideMotherFamilyName'];
    const missingFields = requiredFields.filter(field => !formData[field as keyof typeof formData]);
    if (formData.priest === 'Other' && !formData.priestOther.trim()) {
      setSubmitStatus('error'); setSubmitMessage('Please specify who will conduct the engagement ceremony.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return;
    }
    if (missingFields.length > 0) { setSubmitStatus('error'); setSubmitMessage('Please fill in all required fields marked with *'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => { if (key !== 'priestOther' && key !== 'locationOther' && value) formDataToSend.append(key, value); });
      if (formData.priest === 'Other' && formData.priestOther) formDataToSend.append('priest', formData.priestOther);
      if (formData.location === 'Other Location' && formData.locationOther) formDataToSend.append('custom_location', formData.locationOther);
      formDataToSend.append('posterOption', posterMode);
      if (posterMode === 'upload') {
        formDataToSend.append('customPoster', customPosterFile ? 'Uncollectable' : 'Not provided');
      } else {
        if (builderData.hasPhoto) formDataToSend.append('announcementPhoto', 'Uncollectable');
        if (builderData.customMessage) formDataToSend.append('announcementMessage', builderData.customMessage);
      }
      const response = await submitLocalForm('Engagement Form', formDataToSend);
      if (response.ok) {
        setSubmitStatus('success');
        setSubmitMessage('Your engagement form has been submitted successfully! We will contact you soon to confirm the details.');
        setFormData({ bridegroomName: '', bridegroomFamilyName: '', bridegroomFatherName: '', bridegroomFatherFamilyName: '', bridegroomMotherName: '', bridegroomMotherFamilyName: '', brideName: '', brideFamilyName: '', brideFatherName: '', brideFatherFamilyName: '', brideMotherName: '', brideMotherFamilyName: '', location: 'Saint Mina & Pope Cyril VI COC', priest: 'Father Anthony', priestOther: '', locationOther: '', eventDate: '', eventTime: '' });
        setBuilderData({ photoDataUrl: '', customMessage: '', imagePosition: 'center', themeIndex: 0, hasPhoto: false });
        setCustomPosterFile(null);
        setPosterMode('builder');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else { throw new Error('Submission failed'); }
    } catch { setSubmitStatus('error'); setSubmitMessage('There was an error submitting your form. Please try again or contact us directly.'); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  return (
    <PageLayout>
      <PageHero title="Engagement Form" subtitle="Request an engagement ceremony" />
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-12 text-center">
          <div className="inline-block bg-burgundy-50 px-8 py-6 rounded-lg">
            <p className="text-lg italic text-gray-700 mb-2">"He who finds a wife finds a good thing and obtains favor from the Lord."</p>
            <p className="text-sm font-medium text-burgundy-700">— Proverbs 18:22</p>
          </div>
        </div>
        {submitStatus === 'success' && (
          <div className="mb-8 bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg">
            <div className="flex items-start"><i className="ri-checkbox-circle-fill text-2xl mr-3 mt-0.5"></i><p className="text-sm leading-relaxed">{submitMessage}</p></div>
          </div>
        )}
        {submitStatus === 'error' && (
          <div className="mb-8 bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg">
            <div className="flex items-start"><i className="ri-error-warning-fill text-2xl mr-3 mt-0.5"></i><p className="text-sm leading-relaxed">{submitMessage}</p></div>
          </div>
        )}
        <form id="engagement-form" data-local-form onSubmit={handleSubmit} className="space-y-12">
          {/* Bridegroom Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center"><i className="ri-user-3-line text-gold-600 mr-3"></i>Bridegroom Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[['bridegroomName',"Bridegroom's Name"],['bridegroomFamilyName',"Bridegroom's Family Name"],['bridegroomFatherName',"Bridegroom's Father's Name"],['bridegroomFatherFamilyName',"Bridegroom's Father's Family Name"],['bridegroomMotherName',"Bridegroom's Mother's Name"],['bridegroomMotherFamilyName',"Bridegroom's Mother's Family Name"]].map(([name,label]) => (
                <div key={name}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{label} <span className="text-red-500">*</span></label>
                  <input type="text" name={name} value={formData[name as keyof typeof formData]} onChange={handleInputChange} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
                </div>
              ))}
            </div>
          </div>
          {/* Bride Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center"><i className="ri-user-3-line text-gold-600 mr-3"></i>Bride Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[['brideName',"Bride's Name"],['brideFamilyName',"Bride's Family Name"],['brideFatherName',"Bride's Father's Name"],['brideFatherFamilyName',"Bride's Father's Family Name"],['brideMotherName',"Bride's Mother's Name"],['brideMotherFamilyName',"Bride's Mother's Family Name"]].map(([name,label]) => (
                <div key={name}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{label} <span className="text-red-500">*</span></label>
                  <input type="text" name={name} value={formData[name as keyof typeof formData]} onChange={handleInputChange} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
                </div>
              ))}
            </div>
          </div>
          {/* Church Event Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center"><i className="ri-church-line text-gold-600 mr-3"></i>Church Event Information</h2>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location <span className="text-red-500">*</span></label>
                  <select name="location" value={formData.location} onChange={handleInputChange} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 cursor-pointer">
                    <option value="Saint Mina & Pope Cyril VI COC">Saint Mina &amp; Pope Cyril VI COC</option>
                    <option value="Other Location">Other Location</option>
                  </select>
                  {formData.location === 'Other Location' && (
                    <input
                      type="text"
                      name="locationOther"
                      value={formData.locationOther}
                      onChange={handleInputChange}
                      placeholder="Please specify the location"
                      className="mt-3 w-full px-4 py-3 text-sm border border-gold-400 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Conducted By <span className="text-red-500">*</span></label>
                  <select name="priest" value={formData.priest} onChange={handleInputChange} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 cursor-pointer">
                    <option value="Father Anthony">Father Anthony</option>
                    <option value="Other">Other</option>
                  </select>
                  {formData.priest === 'Other' && (
                    <input
                      type="text"
                      name="priestOther"
                      value={formData.priestOther}
                      onChange={handleInputChange}
                      placeholder="Please specify who will conduct the ceremony"
                      className="mt-3 w-full px-4 py-3 text-sm border border-gold-400 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date <span className="text-gray-400 font-normal">(subject to approval)</span></label>
                <input type="date" name="eventDate" value={formData.eventDate} onChange={handleInputChange} className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time <span className="text-gray-400 font-normal">(subject to approval)</span></label>
                <input type="time" name="eventTime" value={formData.eventTime} onChange={handleInputChange} className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500" />
              </div>
              <div className="bg-burgundy-50 border border-burgundy-200 rounded-lg p-4 md:col-span-2">
                <div className="flex items-start"><i className="ri-information-line text-burgundy-700 text-xl mr-3 mt-0.5"></i><p className="text-sm text-burgundy-800"><strong>Please note:</strong> Nothing is confirmed until Father Anthony has approved the requested date and time, unless it has already been confirmed beforehand. We will contact you to finalize the scheduling.</p></div>
              </div>
            </div>
          </div>

          {/* Poster / Announcement Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 flex items-center"><i className="ri-image-line text-gold-600 mr-3"></i>Announcement Poster</h2>
            <p className="text-sm text-gray-500 mb-6">Choose how you'd like to create your engagement announcement.</p>

            {/* Mode Toggle */}
            <div className="flex gap-4 mb-8">
              <button
                type="button"
                onClick={() => setPosterMode('builder')}
                className={`flex-1 flex items-center justify-center gap-2 px-5 py-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${posterMode === 'builder' ? 'border-burgundy-700 bg-burgundy-50 text-burgundy-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <i className="ri-magic-line text-lg"></i>
                <span>Use Announcement Builder</span>
                {posterMode === 'builder' && <i className="ri-check-line text-burgundy-700 ml-1"></i>}
              </button>
              <button
                type="button"
                onClick={() => setPosterMode('upload')}
                className={`flex-1 flex items-center justify-center gap-2 px-5 py-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${posterMode === 'upload' ? 'border-burgundy-700 bg-burgundy-50 text-burgundy-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <i className="ri-upload-cloud-line text-lg"></i>
                <span>Upload My Own Poster</span>
                {posterMode === 'upload' && <i className="ri-check-line text-burgundy-700 ml-1"></i>}
              </button>
            </div>

            {/* Upload Mode */}
            {posterMode === 'upload' && (
              <div>
                <p className="text-sm text-gray-600 mb-4">Upload your own custom-designed engagement poster. Accepted formats: JPG, PNG, PDF.</p>
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gold-500 hover:bg-gold-50 transition-colors">
                  {customPosterFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <i className="ri-file-check-line text-4xl text-gold-600"></i>
                      <span className="text-sm font-medium text-burgundy-700">{customPosterFile.name}</span>
                      <span className="text-xs text-gray-500">Click to replace</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <i className="ri-upload-cloud-2-line text-4xl"></i>
                      <span className="text-sm font-medium">Click to upload your poster</span>
                      <span className="text-xs">JPG, PNG, PDF up to 10MB</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) => setCustomPosterFile(e.target.files?.[0] ?? null)}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Announcement Builder (shown only in builder mode) */}
          {posterMode === 'builder' && (
            <AnnouncementBuilder
              type="engagement"
              name1={formData.bridegroomName}
              familyName1={formData.bridegroomFamilyName}
              name2={formData.brideName}
              familyName2={formData.brideFamilyName}
              eventDate={formData.eventDate}
              eventLocation={formData.location}
              onChange={setBuilderData}
            />
          )}

          <div className="flex justify-center pt-6">
            <button type="submit" className="bg-burgundy-700 text-white px-12 py-4 rounded-lg font-medium hover:bg-burgundy-800 transition-colors duration-200 flex items-center whitespace-nowrap cursor-pointer">
              <i className="ri-send-plane-fill mr-2"></i>Submit Engagement Form
            </button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
}
